// Single source of truth for coach positions across the app.
//
// Both the "New Coach" profile form (which stores the human label in the
// coach's free-text `title` column) and the "New Term Sheet" wizard (which
// stores the snake_case `value` in the contract's `contract_type` enum) read
// from this list, so the two screens can never drift apart again.
//
// `value` MUST stay in sync with the `contract_type` Postgres enum
// (lib/db/src/schema/contracts.ts) and the `contractType` enum in the OpenAPI
// spec (lib/api-spec/openapi.yaml). When adding a position here, also add its
// `value` to those two places (and a migration under lib/db/src/migrations).

export interface CoachPosition {
  /** snake_case slug — persisted to the `contract_type` enum. */
  value: string;
  /** Human-readable label — persisted to the coach `title` text column. */
  label: string;
}

export const COACH_POSITIONS = [
  { value: "head_coach", label: "Head Coach" },
  { value: "associate_head_coach", label: "Associate Head Coach" },
  { value: "assistant_coach", label: "Assistant Coach" },
  { value: "coordinator", label: "Coordinator" },
  { value: "offensive_coordinator", label: "Offensive Coordinator" },
  { value: "defensive_coordinator", label: "Defensive Coordinator" },
  { value: "special_teams_coordinator", label: "Special Teams Coordinator" },
  { value: "graduate_assistant", label: "Graduate Assistant" },
  { value: "interim_head_coach", label: "Interim Head Coach" },
  { value: "volunteer_assistant", label: "Volunteer Assistant" },
  { value: "director_of_operations", label: "Director of Operations" },
  { value: "strength_conditioning_coach", label: "Strength & Conditioning Coach" },
] as const;

export type CoachPositionValue = (typeof COACH_POSITIONS)[number]["value"];

/**
 * The position `value`s as a non-empty tuple, suitable for `z.enum(...)`.
 * Kept in literal-union form (not `string[]`) so form schemas infer the exact
 * `CoachPositionValue` union and stay assignable to the generated API types.
 */
export const COACH_POSITION_VALUES = COACH_POSITIONS.map((p) => p.value) as [
  CoachPositionValue,
  ...CoachPositionValue[],
];

const LABEL_BY_VALUE = new Map<string, string>(COACH_POSITIONS.map((p) => [p.value, p.label]));

/**
 * Resolve a stored `contract_type` value to its display label. Falls back to a
 * de-snaked title-case rendering so legacy values (e.g. the original bare
 * `interim`) still display sensibly.
 */
export function coachPositionLabel(value: string): string {
  return (
    LABEL_BY_VALUE.get(value) ??
    value.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase())
  );
}

const VALUE_SET = new Set<string>(COACH_POSITION_VALUES);

/**
 * Coerce a stored `contract_type` value into a selectable position so existing
 * documents can pre-fill the dropdown. The original bare `interim` value (still
 * valid in the DB enum) maps to `interim_head_coach`; anything unrecognized
 * falls back to `head_coach`.
 */
export function toSelectablePosition(value: string | null | undefined): CoachPositionValue {
  if (value && VALUE_SET.has(value)) return value as CoachPositionValue;
  if (value === "interim") return "interim_head_coach";
  return "head_coach";
}
